import {
    createSlice,
    createAsyncThunk
} from '@reduxjs/toolkit'
import api from '../../api/api'

export const fetch_chat_unread_count = createAsyncThunk(
    'chat/fetch_unread_count',
    async (_, { fulfillWithValue }) => {
        try {
            const { data } = await api.get('/chat/customer/unread-count')
            return fulfillWithValue(Number(data?.unreadCount) || 0)
        } catch (_) {
            return fulfillWithValue(0)
        }
    }
)

export const search_chat_identities = createAsyncThunk(
    'chat/search_identities',
    async (query, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get('/chat/search', { params: { q: query } })
            return fulfillWithValue(data?.results || [])
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Axtarış nəticələri yüklənmədi.' })
        }
    }
)

export const fetch_direct_conversations = createAsyncThunk(
    'chat/fetch_direct_conversations',
    async (_, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get('/chat/direct/conversations')
            return fulfillWithValue(data?.conversations || [])
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Birbaşa söhbətlər yüklənmədi.' })
        }
    }
)

export const fetch_chat_announcements = createAsyncThunk(
    'chat/fetch_announcements',
    async (_, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get('/chat/customer/announcements')
            return fulfillWithValue(data || {})
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Bildiriş mesajları yüklənmədi.' })
        }
    }
)

export const mark_chat_announcement_read = createAsyncThunk(
    'chat/mark_announcement_read',
    async (announcementId, { fulfillWithValue }) => {
        await api.patch(`/chat/customer/announcements/${announcementId}/read`)
        return fulfillWithValue(announcementId)
    }
)

export const start_direct_chat = createAsyncThunk(
    'chat/start_direct_chat',
    async (targetCustomerId, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post('/chat/direct/start', { targetCustomerId })
            return fulfillWithValue(data?.conversation)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Söhbət başlatmaq mümkün olmadı.' })
        }
    }
)

export const fetch_direct_messages = createAsyncThunk(
    'chat/fetch_direct_messages',
    async ({ conversationId, cursor, limit = 30 }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/chat/direct/conversations/${conversationId}/messages`, { params: { cursor, limit } })
            return fulfillWithValue({ ...data, conversationId })
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Mesajlar yüklənmədi.' })
        }
    }
)

export const send_direct_message = createAsyncThunk(
    'chat/send_direct_message',
    async ({ conversationId, text }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(`/chat/direct/conversations/${conversationId}/messages`, { text })
            return fulfillWithValue(data?.message)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Mesaj göndərilmədi.' })
        }
    }
)

export const mark_direct_read = createAsyncThunk(
    'chat/mark_direct_read',
    async (conversationId) => {
        await api.patch(`/chat/direct/conversations/${conversationId}/read`)
        return conversationId
    }
)

export const add_friend = createAsyncThunk(
    'chat/add_friend',
    async (info, { fulfillWithValue, rejectWithValue, getState }) => {
        try {
            const { cursor, limit = 30, ...body } = info || {}
            const {
                data
            } = await api.post('/chat/customer/add-customer-friend', body, { params: { cursor, limit } })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const send_message = createAsyncThunk(
    'chat/send_message',
    async (info, { fulfillWithValue, rejectWithValue,getState }) => {
        try {
            const {
                data
            } = await api.post('/chat/customer/send-message-to-seller', info)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: 'Mesaj göndərilmədi' })
        }
    }
)

// Friend-i siyahının əvvəlinə çəkir (mutasiya etmədən yeni massiv qaytarır)
const bubbleFriendToTop = (friends, fdId, patch = {}) => {
    const list = Array.isArray(friends) ? [...friends] : []
    const idx = list.findIndex(f => f.fdId === fdId)
    if (idx === -1) return list
    const [target] = list.splice(idx, 1)
    return [{ ...target, ...patch }, ...list]
}

export const chatReducer = createSlice({
    name: 'chat',
    initialState: {
        my_friends: [],
        fd_messages: [],
        currentFd: "",
        successMessage: "",
        errorMessage: "",
        lastSentMessage: null,
        unreadCount: 0,
        nextCursor: null,
        hasMore: false,
        historyLoading: false,
        historyError: '',
        searchResults: [],
        searchLoading: false,
        searchError: '',
        directConversations: [],
        directMessages: [],
        currentDirect: null,
        directNextCursor: null,
        directHasMore: false,
        directHistoryLoading: false,
        directHistoryError: '',
        directSendLoading: false,
        chatAnnouncements: [],
        chatAnnouncementUnread: 0,
        chatAnnouncementsLoading: false,
        chatAnnouncementsError: ''
    },
    reducers: {
        messageClear: (state, _) => {
            state.errorMessage = ''
            state.successMessage = ''
            state.lastSentMessage = null
        },
        // Socket-dən gələn satıcı mesajı — _id/tempId üzrə dedupe
        updateMessage: (state, { payload }) => {
            if (!payload) return
            const exists = state.fd_messages.some(m =>
                (payload._id && m._id === payload._id) ||
                (payload.tempId && m.tempId && m.tempId === payload.tempId)
            )
            if (exists) return
            state.fd_messages = [...state.fd_messages, payload]
        },
        addNewFriend: (state, { payload }) => {
            const exists = state.my_friends.some(f => f.fdId === payload.fdId)
            if (!exists) {
                state.my_friends = [payload, ...state.my_friends]
            }
        },
        // Uğursuz müvəqqəti mesajı silmək üçün (yenidən cəhddən əvvəl)
        removeTempMessage: (state, { payload }) => {
            state.fd_messages = state.fd_messages.filter(m => m.tempId !== payload)
        },
        resetChatUnread: (state) => {
            state.unreadCount = 0
        },
        clearChatSearch: (state) => {
            state.searchResults = []
            state.searchLoading = false
            state.searchError = ''
        }
    },
    extraReducers: (builder) => {
    builder
        .addCase(fetch_chat_unread_count.fulfilled, (state, { payload }) => {
            state.unreadCount = payload
        })
        .addCase(search_chat_identities.pending, (state) => {
            state.searchLoading = true
            state.searchError = ''
        })
        .addCase(search_chat_identities.fulfilled, (state, { payload }) => {
            state.searchResults = payload
            state.searchLoading = false
        })
        .addCase(search_chat_identities.rejected, (state, { payload }) => {
            state.searchResults = []
            state.searchLoading = false
            state.searchError = payload?.error || 'Axtarış nəticələri yüklənmədi.'
        })
        .addCase(fetch_direct_conversations.fulfilled, (state, { payload }) => {
            state.directConversations = payload
        })
        .addCase(fetch_chat_announcements.pending, (state) => {
            state.chatAnnouncementsLoading = true
            state.chatAnnouncementsError = ''
        })
        .addCase(fetch_chat_announcements.fulfilled, (state, { payload }) => {
            state.chatAnnouncements = payload.announcements || []
            state.chatAnnouncementUnread = state.chatAnnouncements.filter(item => !item.isRead).length
            state.chatAnnouncementsLoading = false
        })
        .addCase(fetch_chat_announcements.rejected, (state, { payload }) => {
            state.chatAnnouncementsLoading = false
            state.chatAnnouncementsError = payload?.error || 'Bildiriş mesajları yüklənmədi.'
        })
        .addCase(mark_chat_announcement_read.fulfilled, (state, { payload }) => {
            state.chatAnnouncements = state.chatAnnouncements.map(item => item._id === payload
                ? { ...item, isRead: true, readAt: item.readAt || new Date().toISOString() }
                : item)
            state.chatAnnouncementUnread = state.chatAnnouncements.filter(item => !item.isRead).length
        })
        .addCase(start_direct_chat.fulfilled, (state, { payload }) => {
            if (!payload) return
            const existing = state.directConversations.findIndex(item => item._id === payload._id)
            if (existing === -1) state.directConversations = [payload, ...state.directConversations]
            else state.directConversations[existing] = { ...state.directConversations[existing], ...payload }
            state.currentDirect = payload
        })
        .addCase(fetch_direct_messages.pending, (state, { meta }) => {
            state.directHistoryLoading = true
            if (!meta.arg?.cursor) state.directHistoryError = ''
        })
        .addCase(fetch_direct_messages.fulfilled, (state, { payload, meta }) => {
            const incoming = payload.messages || []
            if (meta.arg?.cursor) {
                const ids = new Set(state.directMessages.map(item => String(item._id)))
                state.directMessages = [...incoming.filter(item => !ids.has(String(item._id))), ...state.directMessages]
            } else {
                state.directMessages = incoming
                state.currentDirect = state.directConversations.find(item => item._id === payload.conversationId) || state.currentDirect
            }
            state.directNextCursor = payload.nextCursor || null
            state.directHasMore = Boolean(payload.hasMore)
            state.directHistoryLoading = false
            state.directHistoryError = ''
        })
        .addCase(fetch_direct_messages.rejected, (state, { payload }) => {
            state.directHistoryLoading = false
            state.directHistoryError = payload?.error || 'Mesajlar yüklənmədi.'
        })
        .addCase(send_direct_message.pending, (state) => {
            state.directSendLoading = true
        })
        .addCase(send_direct_message.fulfilled, (state, { payload }) => {
            state.directSendLoading = false
            if (!payload) return
            state.directMessages.push(payload)
            state.directConversations = state.directConversations.map(item => item._id === payload.conversationId
                ? { ...item, lastMessage: payload.text, lastMessageAt: payload.createdAt }
                : item)
        })
        .addCase(send_direct_message.rejected, (state, { payload }) => {
            state.directSendLoading = false
            state.errorMessage = payload?.error || 'Mesaj göndərilmədi.'
        })
        .addCase(mark_direct_read.fulfilled, (state, { payload }) => {
            const conversation = state.directConversations.find(item => item._id === payload)
            state.unreadCount = Math.max(0, state.unreadCount - (Number(conversation?.unreadCount) || 0))
            state.directConversations = state.directConversations.map(item => item._id === payload ? { ...item, unreadCount: 0 } : item)
        })
        .addCase(add_friend.pending, (state, { meta }) => {
            if (meta.arg?.cursor) state.historyLoading = true
            if (!meta.arg?.cursor) state.historyError = ''
        })
        .addCase(add_friend.fulfilled, (state, { payload, meta }) => {
            const incoming = payload.messages || []
            if (meta.arg?.cursor) {
                const existingIds = new Set(state.fd_messages.map(message => String(message._id || message.tempId)))
                state.fd_messages = [...incoming.filter(message => !existingIds.has(String(message._id || message.tempId))), ...state.fd_messages]
            } else {
                state.fd_messages = incoming
            }
            state.currentFd = payload.currentFd;
            state.my_friends = payload.myFriends || [];
            state.nextCursor = payload.nextCursor || null
            state.hasMore = Boolean(payload.hasMore)
            state.historyLoading = false
            state.historyError = ''
        })
        .addCase(add_friend.rejected, (state, { meta }) => {
            state.historyLoading = false
            if (!meta.arg?.cursor) state.historyError = 'Mesajlar yüklənmədi'
        })

        // ── Optimistik göndərmə ──────────────────────────────────────────────
        .addCase(send_message.pending, (state, { meta }) => {
            const { userId, sellerId, text, name, tempId } = meta.arg || {}
            if (!tempId) return
            const existing = state.fd_messages.find(m => m.tempId === tempId)
            if (existing) {
                // retry: mövcud temp-i yenidən "göndərilir" vəziyyətinə qaytar
                existing.status = 'pending'
                existing.sending = true
                existing.failed = false
                return
            }
            state.fd_messages = [...state.fd_messages, {
                _id: tempId,
                tempId,
                senderId: String(userId),
                receverId: sellerId,
                senderRole: 'customer',
                senderName: name,
                message: text,
                status: 'pending',
                sending: true,
                failed: false,
                createdAt: new Date().toISOString()
            }]
            state.my_friends = bubbleFriendToTop(state.my_friends, sellerId, {
                lastMessage: text,
                lastTime: new Date().toISOString()
            })
        })

        .addCase(send_message.fulfilled, (state, { payload, meta }) => {
            const tempId = meta.arg?.tempId
            const saved = payload.message
            if (!saved) return
            const idx = state.fd_messages.findIndex(m => tempId && m.tempId === tempId)
            const merged = { ...saved, tempId, status: 'sent', sending: false, failed: false }
            if (idx !== -1) {
                state.fd_messages[idx] = merged
            } else if (!state.fd_messages.some(m => m._id === saved._id)) {
                state.fd_messages = [...state.fd_messages, merged]
            }
            state.my_friends = bubbleFriendToTop(state.my_friends, saved.receverId, {
                lastMessage: saved.message,
                lastTime: saved.createdAt
            })
            state.successMessage = 'message send success'
            state.lastSentMessage = merged
        })

        .addCase(send_message.rejected, (state, { meta, payload }) => {
            const tempId = meta.arg?.tempId
            const temp = state.fd_messages.find(m => tempId && m.tempId === tempId)
            if (temp) {
                temp.sending = false
                temp.failed = true
                temp.status = 'failed'
            }
            state.errorMessage = payload?.error || payload?.message || 'Mesaj göndərilmədi'
        })
}
})

export const {
    messageClear,
    updateMessage,
    addNewFriend,
    removeTempMessage,
    resetChatUnread,
    clearChatSearch
} = chatReducer.actions
export default chatReducer.reducer
